import NavigationIcon from "@mui/icons-material/Navigation";
import SquareRoundedIcon from "@mui/icons-material/SquareRounded";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import useTheme from "@mui/material/styles/useTheme";
import L from "leaflet";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";

interface MapComponentProps {
  latitude: number;
  longitude: number;
  bearing: number; // degrees, 0 = north
  speed: number; // km/h
  isDriving: boolean;
}

function calculateDistance([lat1, lng1]: [number, number], [lat2, lng2]: [number, number]): number {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function animateMarker(
  marker: L.Marker,
  start: [number, number],
  end: [number, number],
  duration: number,
  onDone?: () => void,
) {
  let startTime: number | null = null;
  const step = (ts: number) => {
    if (!startTime) startTime = ts;
    const t = Math.min((ts - startTime) / duration, 1);
    const lat = start[0] + (end[0] - start[0]) * t;
    const lng = start[1] + (end[1] - start[1]) * t;
    marker.setLatLng([lat, lng]);
    if (t < 1) requestAnimationFrame(step);
    else onDone?.();
  };
  requestAnimationFrame(step);
}

function MapEventHandler({ onUserPan }: { onUserPan: () => void }) {
  // if user drags or zooms, disable auto-follow
  useMapEvents({
    dragstart: onUserPan,
    zoomstart: onUserPan,
  });
  return null;
}

function MapFollower({ position, follow }: { position: [number, number]; follow: boolean }) {
  const map = useMap();
  useEffect(() => {
    if (!follow) return;
    const center = map.getCenter();
    const dLat = position[0] - center.lat;
    const dLng = position[1] - center.lng;
    if (Math.hypot(dLat, dLng) > 0.0005) {
      map.panTo(position, {
        animate: true,
        duration: 1,
        easeLinearity: 0.25,
      });
    }
  }, [position, follow, map]);
  return null;
}

const MapComponent: React.FC<MapComponentProps> = ({
  latitude,
  longitude,
  bearing,
  speed,
  isDriving,
}) => {
  const theme = useTheme();
  const markerRef = useRef<L.Marker>(null);

  const [follow, setFollow] = useState(true);

  // simulation state
  const simRef = useRef<{
    lat: number;
    lng: number;
    bearing: number;
    speedKmh: number;
    lastTime: number | null;
    catchingUp: boolean;
  }>({
    lat: latitude,
    lng: longitude,
    bearing,
    speedKmh: speed,
    lastTime: null,
    catchingUp: false,
  });

  // icon factory
  const createIcon = useCallback(
    (b: number, drive: boolean) => {
      const markup = drive
        ? renderToStaticMarkup(
            <NavigationIcon
              style={{
                color: theme.palette.primary.main,
                fontSize: "2rem",
                transform: `rotate(${b}deg)`,
              }}
            />,
          )
        : renderToStaticMarkup(
            <SquareRoundedIcon
              style={{
                color: theme.palette.primary.main,
                fontSize: "1.5rem",
                transform: `rotate(${b}deg)`,
              }}
            />,
          );
      return L.divIcon({
        html: markup,
        className: "custom-marker",
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });
    },
    [theme.palette.primary.main],
  );

  // 1. continuous dead-reckon loop
  useEffect(() => {
    let rafId: number;
    const loop = (ts: number) => {
      const sim = simRef.current;
      if (sim.lastTime != null && !sim.catchingUp) {
        const dt = (ts - sim.lastTime) / 1000; // s
        const speedMs = sim.speedKmh / 3.6; // m/s
        const dist = speedMs * dt;
        if (dist > 0) {
          const dLat = (dist * Math.cos((sim.bearing * Math.PI) / 180)) / 111320;
          const dLng =
            (dist * Math.sin((sim.bearing * Math.PI) / 180)) /
            (111320 * Math.cos((sim.lat * Math.PI) / 180));
          sim.lat += dLat;
          sim.lng += dLng;
          markerRef.current?.setLatLng([sim.lat, sim.lng]);
        }
      }
      simRef.current.lastTime = ts;
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // 2. catch-up on real GPS fixes only
  useEffect(() => {
    const sim = simRef.current;
    const marker = markerRef.current;
    if (!marker) return;
    // 1) if we’re already catching up, skip this fix (or you could queue it up)
    if (sim.catchingUp) {
      return;
    }

    // compute gap from the marker’s _actual_ current position
    const current = marker.getLatLng();
    const from: [number, number] = [current.lat, current.lng];
    const to: [number, number] = [latitude, longitude];
    const gap = calculateDistance(from, to);

    if (gap > 0) {
      sim.catchingUp = true;
      // catch up at 3× speed (but not <1 m/s)
      const speedMs = sim.speedKmh / 3.6;
      const catchMs = Math.max(speedMs * 3, 1);
      const duration = (gap / catchMs) * 1000;
      animateMarker(marker, from, to, duration, () => {
        // after catch-up, reset sim
        sim.lat = latitude;
        sim.lng = longitude;
        sim.bearing = bearing;
        sim.speedKmh = speed;
        sim.lastTime = null;
        sim.catchingUp = false;
      });
    } else {
      // already in sync
      sim.lat = latitude;
      sim.lng = longitude;
      sim.bearing = bearing;
      sim.speedKmh = speed;
      sim.lastTime = null;
    }
  }, [latitude, longitude, bearing, speed]);

  // 3. icon updates (bearing ↔ square), no position resets here
  useEffect(() => {
    markerRef.current?.setIcon(createIcon(bearing, isDriving));
  }, [bearing, isDriving, createIcon]);

  return (
    <Paper sx={{ width: "100%", height: "30vh" }}>
      <MapContainer
        center={[latitude, longitude]}
        zoom={15}
        style={{ width: "100%", height: "100%" }}
      >
        <IconButton
          size="large"
          aria-label="recenter map"
          onClick={() => setFollow(true)}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1000,
            bgcolor: "background.paper",
            boxShadow: 1,
          }}
        >
          <NavigationIcon />
        </IconButton>
        <MapEventHandler onUserPan={() => setFollow(false)} />

        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        <Marker
          position={[latitude, longitude]}
          icon={createIcon(bearing, isDriving)}
          ref={markerRef}
        >
          {/* <Popup>
            Pos: [{latitude.toFixed(4)}, {longitude.toFixed(4)}] <br />
            Bearing: {bearing.toFixed(1)}° <br />
            Speed: {speed.toFixed(1)} km/h
          </Popup> */}
        </Marker>
        <MapEventHandler onUserPan={() => setFollow(false)} />
        <MapFollower position={[simRef.current.lat, simRef.current.lng]} follow={follow} />
      </MapContainer>
    </Paper>
  );
};

export default MapComponent;
